// src/lib/fbShareContacts.js — which name+number pairs leave with a Facebook share.
//
// OWNER (OWNER_REVIEW_2026-08-25 §7): "We only share name and number." And, on why there may be
// several: "Point of sharing many numbers is that if one doesn't answer another will, so client
// won't be lost."
//
// ⚠️ PURE, AND SEPARATE FROM THE PICKER, because this is the last gate before a phone number
// becomes a public Facebook post. Inside a component it would only ever be exercised by clicking,
// and the cases that matter — a stale tick left behind after the list reloaded, a contact with no
// number, the typed box duplicating someone already chosen — are exactly the ones nobody clicks.
//
// 🔴 WHAT THIS FILE MAY NOT DO: filter opt-outs. The server has already removed them
// (`GET /api/core/property/:id/share-contacts` → `property_agent_optout_v1`), and a second
// implementation of that rule here would drift — with the drifting copy being the one that
// publishes a number somebody asked us not to publish. If an opted-out agent ever appears in a
// post, the bug is in the route or in something that bypassed it, never a missing filter here.

// A picked id that is no longer in the list is DROPPED rather than carried. The list reloads when
// the property resolves, and a tick left over from a previous list is a checkbox pointing at
// somebody the sharer can no longer see — including, after an opt-out, somebody the server has
// just removed. Ticks are intent about a NAMED person, so they expire with the name.
export function fbShareContacts(contacts, picked, typed = '') {
  const list = Array.isArray(contacts) ? contacts : []
  const want = new Set(Array.isArray(picked) ? picked : [])
  const out = []
  const seen = new Set()

  for (const c of list) {
    if (!want.has(c?.party_id)) continue
    const phone = String(c?.phone || '').trim()
    // No number means nothing to publish. The picker shows and disables these rows so the
    // absence is visible; here they simply cannot become a line in a post.
    if (!phone) continue
    const key = normalizePhone(phone)
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ name: String(c?.name || '').trim() || null, phone })
  }

  // The typed box is an EXTRA, not a replacement: it is how an admin adds a number the roster
  // does not hold. Unnamed on purpose — we publish what we know, and inventing a name for a typed
  // number would attach a person to digits nobody has checked.
  const extra = String(typed || '').trim()
  if (extra && !seen.has(normalizePhone(extra))) out.push({ name: null, phone: extra })

  return out
}

// Digits only, so 555 12 34 56 and 555123456 are recognised as one number rather than published
// twice under two names. Deliberately naive: it decides DUPLICATES, never validity — rejecting a
// number here would silently drop a contact the sharer deliberately chose.
function normalizePhone(s) {
  return String(s).replace(/\D+/g, '')
}
